<template>
  <div class="buy-go-component">
    <div class="row mb-3">
      <div class="col-lg-6">
        <AiChatCard
          :companyId="props.companyId"
          :endpoint="`/api/company/${props.companyId}/portfolio/buy-chat`"
          :greeting="$t('ai_chat.greetings.buy')"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>
      <div class="col-lg-6">
        <SourcingPreferenceCard
          v-model="globalSettings"
          @user-select="userSelectedSourcingMode = true"
        />
      </div>
    </div>

    <CustomRequirementCard
      v-if="isOtherMode"
      v-model="globalSettings.custom_requirement"
    />

    <div class="card mt-3">
      <div class="card-header">
        <h5 class="mb-1">{{ $t("go_buy.price_breakdown_title") }}</h5>
        <p class="text-muted mb-0">
          {{ $t("go_buy.price_breakdown_description") }}
        </p>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end border-bottom pb-4 mb-4">
          <div class="col-md-4 col-lg-3">
            <label class="form-label">
              {{ $t("go_buy.form.volume.label") }}
            </label>
            <input
              v-model.number="volume"
              type="number"
              min="1"
              class="form-control"
            />
          </div>
          <div class="col-md-4 col-lg-3">
            <label class="form-label">
              {{ $t("go_buy.certificate_scheme") }}
            </label>
            <select v-model="globalSettings.eac_source" class="form-select">
              <option
                v-for="source in formOptions?.eac_sources"
                :key="source.value"
                :value="source.value"
              >
                {{ source.label }}
              </option>
            </select>
          </div>
          <div v-if="isIrecSource" class="col-md-4 col-lg-3">
            <label :for="sourceCountryId" class="form-label">
              {{ $t("go_buy.source_country") }}
            </label>
            <SearchableSelectComponent
              :inputId="sourceCountryId"
              :modelValue="globalSettings.source_country"
              :options="irecCountryOptions"
              :placeholder="$t('go_buy.any_country')"
              clearOnFocus
              @update:modelValue="selectSourceCountry"
            />
          </div>
          <div class="col-md-auto">
            <label :for="vintageId" class="form-label">
              {{ $t("bulk_buy.vintage") }}
            </label>
            <input
              :id="vintageId"
              :value="globalSettings.vintage"
              type="text"
              class="form-control-plaintext fs-9"
              readonly
            />
          </div>
          <div class="col-md-auto">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="!canAskForQuote"
              @click="fetchPricing"
            >
              {{ $t("go_buy.ask_for_quote_button") }}
            </button>
          </div>
        </div>

        <LoadingIndicatorComponent
          v-if="pricingState.status === 'loading'"
          inline
          :message="pricingState.message"
        />

        <div
          v-else-if="pricingState.status === 'error'"
          class="alert alert-warning mb-0"
        >
          {{ pricingState.message }}
        </div>

        <div
          v-else-if="pricingState.status === 'agent_reach_out'"
          class="alert alert-warning mb-0"
        >
          {{ $t("go_buy.agent_quote_prompt") }}
        </div>

        <div v-else-if="pricingDetails" class="row g-4">
          <div class="col-12">
            <h6 class="mb-3">{{ $t("go_buy.purchase_summary") }}</h6>
            <div class="bg-light rounded-3 p-3">
              <div class="d-flex justify-content-between gap-3 flex-wrap">
                <span class="fw-semibold">
                  {{ $t("go_buy.volume_to_buy") }}
                </span>
                <span>{{ volume }} MWh</span>
              </div>
              <div class="d-flex justify-content-between gap-3 flex-wrap mt-2">
                <span class="fw-semibold">
                  {{ $t("company_orders.buy.total_price") }}
                </span>
                <span class="fw-semibold">
                  {{ formatEuro(pricingDetails.price) }}
                </span>
              </div>
              <div
                class="indicative-note text-secondary small mt-2 d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon
                  :icon="['far', 'circle-info']"
                  class="flex-shrink-0"
                />
                {{ $t("bulk_buy.quotes_indicative_note") }}
              </div>
              <div
                v-if="quoteProvenance"
                class="quote-provenance text-secondary small mt-1 font-monospace"
              >
                {{ quoteProvenance }}
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-muted">
          {{ $t("bulk_buy.pricing_idle") }}
        </div>

        <div class="row g-3 align-items-end mt-1">
          <div class="col-lg">
            <label class="form-label">
              {{ $t("go_buy.form.operation_notes.label") }}
            </label>
            <textarea
              v-model="operationNotes"
              class="form-control"
              rows="2"
              :placeholder="$t('go_buy.form.operation_notes.placeholder')"
            />
          </div>
          <div class="col-lg-auto">
            <button
              type="button"
              class="btn btn-success"
              :disabled="!canSubmit"
              @click="submitBuyOrder"
            >
              {{ $t("go_buy.send_purchase_request_button") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { formatEuro } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { sortBy } from "lodash-es";
import { computed, inject, ref, useId, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import type { GlobalPurchaseSettings } from "../bulk_buy/bulkBuyTypes";
import {
  CodRequirement,
  countriesWithEacType,
  EACSource,
  Ecolabel,
  IREC_REGISTRATION_AUTHORITY,
  marketDataDateLabel,
  PurchaseMode,
  Support,
} from "../bulk_buy/bulkBuyTypes";
import { mergeAiGlobalSettings } from "../bulk_buy/bulkBuyAiMapping";
import CustomRequirementCard from "../bulk_buy/CustomRequirementCard.vue";
import SourcingPreferenceCard from "../bulk_buy/SourcingPreferenceCard.vue";
import { useBulkBuyOptions } from "../bulk_buy/useBulkBuyOptions";
import AiChatCard from "../common/AiChatCard.vue";
import { useBuyPricing } from "./useBuyPricing";

const props = defineProps<{
  companyId: number;
  company: Company;
}>();

// Quotes are for the current year's production; other vintages go through a custom (OTHER) request
// that keeps the requested vintage.
const CURRENT_VINTAGE = String(new Date().getFullYear());

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const vintageId = useId();
const sourceCountryId = useId();
const { t, te } = useI18n();
const { options: formOptions } = useBulkBuyOptions();

const globalSettings = ref<GlobalPurchaseSettings>({
  purchase_mode: PurchaseMode.CHEAPEST,
  eac_source: EACSource.STANDARD_AIB,
  technology: "ANY",
  ecolabel: Ecolabel.NONE,
  support: Support.ANY,
  cod_requirement: CodRequirement.ANY,
  vintage: CURRENT_VINTAGE,
  hourly_matching: false,
  monthly_matching: false,
});

const volume = ref(1000);
const operationNotes = ref("");
const uploadedFileId = ref<number | null>(null);
const analysisFormResponse = ref<Record<string, unknown> | null>(null);
const isSubmitting = ref(false);
const userSelectedSourcingMode = ref(false);
// The `sourcing preference:` / `vintage:` segments this page wrote into the custom requirement for
// an analysed document, so the next document replaces exactly those and never the buyer's own text.
const generatedRequirementLabels = ref<string[]>([]);

const prefillBuyVolume = inject<Ref<number | null>>(
  "prefillBuyVolume",
  ref(null)
);

watch(
  prefillBuyVolume,
  (newVolume) => {
    if (newVolume && newVolume > 0) {
      volume.value = newVolume;
      prefillBuyVolume.value = null;
    }
  },
  { immediate: true }
);

// A plain buy is priced by its certificate scheme, so a company without a country can still buy.
const companyCountry = computed(() => props.company.country ?? undefined);
const { pricingState, fetchPricing, clearPricing } = useBuyPricing(
  volume,
  companyCountry,
  globalSettings
);

const isOtherMode = computed(
  () => globalSettings.value.purchase_mode === PurchaseMode.OTHER
);

const isIrecSource = computed(
  () => globalSettings.value.eac_source === EACSource.IREC
);

// "Any country" first (picking it clears the country; an unset one shows the placeholder), then every country an I-REC
// can be issued in, by localized name.
const irecCountryOptions = computed(() => [
  { value: "", label: t("go_buy.any_country") },
  ...sortBy(
    countriesWithEacType(
      formOptions.value?.registration_authorities,
      IREC_REGISTRATION_AUTHORITY
    ).map((country) => ({
      value: country,
      label:
        `${getFlagEmoji(country)} ${translateCountryCode(country, t, te)}`.trim(),
    })),
    "label"
  ),
]);

// Only an RFQ can ask for another vintage; a priced mode is always quoted for the current year.
watch(isOtherMode, (otherMode) => {
  if (!otherMode && globalSettings.value.vintage !== CURRENT_VINTAGE) {
    globalSettings.value = {
      ...globalSettings.value,
      vintage: CURRENT_VINTAGE,
    };
  }
});

// The production country belongs to the I-REC request; another scheme starts without one.
watch(isIrecSource, (irecSource) => {
  if (!irecSource && globalSettings.value.source_country) {
    globalSettings.value = {
      ...globalSettings.value,
      source_country: undefined,
    };
  }
});

// A label stops being the page's once the buyer edits or deletes it, so it is never removed later.
watch(
  () => globalSettings.value.custom_requirement,
  (requirement) => {
    const parts = splitRequirement(requirement);
    generatedRequirementLabels.value = generatedRequirementLabels.value.filter(
      (label) => parts.includes(label)
    );
  }
);

const canAskForQuote = computed(() => {
  // OTHER mode is quoted by hand from the free text, so the backend requires it.
  if (isOtherMode.value && !globalSettings.value.custom_requirement?.trim()) {
    return false;
  }
  return volume.value > 0 && pricingState.value.status !== "loading";
});

const canSubmit = computed(() => {
  if (isSubmitting.value || volume.value < 1) {
    return false;
  }

  return (
    pricingState.value.status === "success" ||
    pricingState.value.status === "agent_reach_out"
  );
});

const pricingDetails = computed(() => {
  return pricingState.value.status === "success" ? pricingState.value : null;
});

// "Market levels as of {date} · Ref {id}" — the quote's audit trail, shown under the total.
const quoteProvenance = computed(() => {
  const details = pricingDetails.value;
  if (!details) return "";
  const parts = [];
  if (details.price_date) {
    parts.push(
      t("bulk_buy.market_levels_as_of", {
        date: marketDataDateLabel(details.price_date),
      })
    );
  }
  if (details.pricing_request_id) {
    parts.push(
      t("bulk_buy.quote_reference", {
        id: details.pricing_request_id.slice(0, 8).toUpperCase(),
      })
    );
  }
  return parts.join(" · ");
});

// Human-readable summary of the AI's structured constraints, used as the custom requirement when
// an extracted ADVANCED request lands on the OTHER card (quoted by hand from free text).
const summarizeExtractedRequirements = (formData: any): string => {
  const constraintFields = [
    "technology",
    "ecolabel",
    "support",
    "vintage",
    "cod_requirement",
    "production_month",
  ] as const;
  const parts = constraintFields
    .filter((field) => formData?.[field])
    .map((field) => `${field.replaceAll("_", " ")}: ${formData[field]}`);
  // Country-specific sourcing is not in the scheme picker — carry it into the free text so the
  // agent quoting the request sees it.
  const source = formData?.eac_source;
  if (source && !(String(source).toUpperCase() in EACSource)) {
    parts.push(`source country: ${source}`);
  }
  return parts.join("; ");
};

// Splits on the separator this page joins with, so the buyer's own text is kept exactly as typed.
const splitRequirement = (requirement?: string): string[] =>
  (requirement?.trim() ?? "").split("; ").filter(Boolean);

/** Swaps the labels generated for an earlier document for `labels`, keeping every other segment. */
const replaceGeneratedLabels = (
  labels: string[],
  writtenThisAnalysis: string[] = []
) => {
  const kept = splitRequirement(globalSettings.value.custom_requirement).filter(
    (part) => !generatedRequirementLabels.value.includes(part)
  );
  // A label the text already holds is the buyer's, so it is not ours to remove, unless this same
  // analysis just wrote it there (the document-summary fallback can produce `vintage: <year>`).
  const added = labels.filter((label) => !kept.includes(label));
  generatedRequirementLabels.value = labels.filter(
    (label) => !kept.includes(label) || writtenThisAnalysis.includes(label)
  );
  globalSettings.value = {
    ...globalSettings.value,
    custom_requirement: [...kept, ...added].join("; ") || undefined,
  };
};

const selectSourceCountry = (country: string | null) => {
  globalSettings.value = {
    ...globalSettings.value,
    source_country: country || undefined,
  };
};

const handleFileAnalysisComplete = (data: any) => {
  if (!data) {
    uploadedFileId.value = null;
    analysisFormResponse.value = null;
    clearPricing();
    return;
  }

  if (data.upload_id) {
    uploadedFileId.value = data.upload_id;
  }
  if (data.form_data) {
    analysisFormResponse.value = data.form_data;
  }

  // Enum-guarded merge shared with the buy & cancel page: maps an extracted ADVANCED to OTHER and
  // applies scheme values (incl. REGO/I-REC); extracted country codes are not schemes and flow
  // into the free-text requirement below instead.
  globalSettings.value = mergeAiGlobalSettings(
    globalSettings.value,
    data.global_settings,
    { preserveUserPurchaseMode: userSelectedSourcingMode.value }
  );
  // Priced quotes are fixed on the current year; a document asking for another vintage goes to an
  // RFQ (OTHER) that keeps that vintage, rather than being quoted for the wrong year. Read the vintage
  // from this analysis, not the merged settings: a later document without one must not inherit the
  // previous document's year and be forced into OTHER again.
  const extractedVintage = String(data.global_settings?.vintage ?? "");
  const needsVintageQuote =
    !!extractedVintage && !extractedVintage.startsWith(CURRENT_VINTAGE);
  const requestedMode = globalSettings.value.purchase_mode;
  const purchaseMode = needsVintageQuote ? PurchaseMode.OTHER : requestedMode;
  globalSettings.value = {
    ...globalSettings.value,
    vintage: needsVintageQuote ? extractedVintage : CURRENT_VINTAGE,
    purchase_mode: purchaseMode,
    // A priced mode drops the OTHER free text, as the sourcing card does on a mode change, so
    // reselecting OTHER cannot resend labels generated for an earlier document.
    custom_requirement:
      purchaseMode === PurchaseMode.OTHER
        ? globalSettings.value.custom_requirement
        : undefined,
  };
  // Labels generated for an earlier document never carry over to this one.
  if (purchaseMode === PurchaseMode.OTHER) {
    replaceGeneratedLabels([]);
  }
  const requirementBeforeDerive = splitRequirement(
    globalSettings.value.custom_requirement
  );
  // OTHER mode needs its free-text requirement; derive it from the document's own words or the
  // extracted constraints so the manual quote request is self-explanatory.
  if (
    globalSettings.value.purchase_mode === PurchaseMode.OTHER &&
    !globalSettings.value.custom_requirement?.trim()
  ) {
    const requirement =
      data.form_data?.operation_notes ||
      summarizeExtractedRequirements(data.form_data);
    if (requirement) {
      globalSettings.value = {
        ...globalSettings.value,
        custom_requirement: requirement,
      };
    }
  }
  // The mode is now OTHER, so the requested vintage and the buyer's sourcing mode must also reach the
  // desk in the free text it quotes from.
  if (needsVintageQuote) {
    const labels = [`vintage: ${extractedVintage}`];
    if (requestedMode !== PurchaseMode.OTHER) {
      labels.unshift(`sourcing preference: ${requestedMode}`);
    }
    const derivedSegments = splitRequirement(
      globalSettings.value.custom_requirement
    ).filter((part) => !requirementBeforeDerive.includes(part));
    replaceGeneratedLabels(labels, derivedSegments);
  }

  if (data.form_data?.volume && data.form_data.volume > 0) {
    volume.value = Math.floor(data.form_data.volume);
  }
  if (data.form_data?.operation_notes) {
    operationNotes.value = data.form_data.operation_notes;
  }

  clearPricing();
};

const submitBuyOrder = async () => {
  if (!canSubmit.value) return;

  try {
    isSubmitting.value = true;

    const pricing =
      pricingState.value.status === "success" ? pricingState.value : null;

    const pricingRequestId =
      pricingState.value.status === "success" ||
      pricingState.value.status === "agent_reach_out"
        ? pricingState.value.pricing_request_id
        : undefined;

    const payload = {
      volume: volume.value,
      country: companyCountry.value,
      operation_notes: operationNotes.value ?? undefined,
      uploaded_file_id: uploadedFileId.value ?? undefined,
      analysis_form_response: analysisFormResponse.value ?? undefined,
      ...globalSettings.value,
      quoted_total_price: pricing?.price,
      pricing_request_id: pricingRequestId,
    };

    await goSolidApi.post(
      `/api/company/${props.companyId}/portfolio/buy-order`,
      payload
    );

    globalSuccessHandler(
      t("go_buy.purchase_request_submitted"),
      t("go_buy.purchase_request_submitted_detail", { volume: volume.value })
    );
    clearPricing();
    operationNotes.value = "";
    uploadedFileId.value = null;
    analysisFormResponse.value = null;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
